import { useEffect, useState } from 'react';
import { salaryService, HrPendingSummary } from '../services/salary.service';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types';

const POLL_INTERVAL_MS = 60_000;

const HR_ROLES = new Set<UserRole>([UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.HR]);

/**
 * Polls the pending-summary endpoint while an HR-capable user is logged in.
 * Returns null when the user has no HR access (no polling, no requests).
 */
export function useHrPendingCounts(): HrPendingSummary | null {
  const { user } = useAuth();
  const [summary, setSummary] = useState<HrPendingSummary | null>(null);

  const userRoles: UserRole[] = user?.roles ?? (user?.role ? [user.role] : []);
  const hasHrAccess = userRoles.some((r) => HR_ROLES.has(r));

  useEffect(() => {
    if (!hasHrAccess) {
      setSummary(null);
      return;
    }

    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const res = await salaryService.getHrPendingSummary();
        if (!cancelled) setSummary(res.summary);
      } catch {
        // Silent on poll failures — don't spam the user
      }
    };

    fetchOnce();
    const id = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [hasHrAccess]);

  return summary;
}
